import React from 'react'

import Script from 'dangerous-html/react'
import { Helmet } from 'react-helmet'

import Navigation from '../components/navigation'
import Footer from '../components/footer'
import './digital-forensics.css'

const DigitalForensics = (props) => {
  return (
    <div className="dfir-container1">
      <Helmet>
        <title>Cysigil Network Pvt Ltd</title>
        <meta
          property="og:title"
          content="Digital Forensics & Incident Response - Cysigil"
        />
        <link
          rel="canonical"
          href="https://cysigil.com/digital-forensics"
        />
      </Helmet>

      <Navigation />

      {/* HERO */}
      <section className="hero-dfir">
        <video
          src="/external/forensic.mp4"
          loop
          muted
          autoPlay
          playsInline
          className="hero-dfir-video"
        />
        <div className="hero-dfir-overlay"></div>
        <div className="hero-dfir-content">
          <div className="hero-dfir-text">
            <h1 className="hero-title">
              Digital Forensics & Incident Response
            </h1>
            <p className="hero-subtitle">
              Rapid investigation, containment, and forensic analysis of cyber
              incidents. Identify root cause, attacker activity, impact scope,
              and support legal, regulatory, and insurance requirements.
            </p>
            <div className="hero-dfir-actions">
              <a href="#contact">
                <div className="btn-primary btn btn-lg">
                  <span>Engage DFIR Team</span>
                </div>
              </a>
              <a href="#contact">
                <div className="btn btn-lg btn-outline">
                  <span style={{ color: 'black' }}>Talk to Expert</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features-dfir">
        <div className="features-dfir-container">
          <div className="features-dfir-header">
            <h2 className="section-title">
              Comprehensive Digital Forensics Scope
            </h2>
            <p className="section-subtitle">
              End-to-end forensic investigation across endpoints, servers,
              cloud, networks, and applications
            </p>
          </div>

          <div className="features-dfir-grid">
            <div className="features-dfir-item">
              <h3 className="features-dfir-title">Incident Triage & Containment</h3>
              <p className="section-content">
                Rapid scoping of the incident, threat containment, and isolation
                to prevent further damage or data loss.
              </p>
            </div>

            <div className="features-dfir-item">
              <h3 className="features-dfir-title">Endpoint & Memory Forensics</h3>
              <p className="section-content">
                Deep analysis of disks, memory, logs, and artifacts from
                compromised endpoints and servers.
              </p>
            </div>

            <div className="features-dfir-item">
              <h3 className="features-dfir-title">Cloud & SaaS Forensics</h3>
              <p className="section-content">
                Investigation of AWS, Azure, GCP, M365, and SaaS logs to detect
                account compromise, misuse, and persistence.
              </p>
            </div>

            <div className="features-dfir-item">
              <h3 className="features-dfir-title">Network Traffic Analysis</h3>
              <p className="section-content">
                PCAP and flow analysis to identify C2 traffic, lateral movement,
                data exfiltration, and attacker infrastructure.
              </p>
            </div>

            <div className="features-dfir-item">
              <h3 className="features-dfir-title">Malware & Ransomware Analysis</h3>
              <p className="section-content">
                Static and dynamic analysis of malware, droppers, loaders, and
                ransomware families.
              </p>
            </div>

            <div className="features-dfir-item">
              <h3 className="features-dfir-title">Legal & Regulatory Support</h3>
              <p className="section-content">
                Evidence handling aligned with chain-of-custody, CERT-In,
                DPDP, GDPR, cyber insurance, and law enforcement requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="process-dfir">
        <div className="process-dfir-container">
          <div className="process-dfir-header">
            <h2 className="section-title">
              Incident Response Methodology
            </h2>
            <p className="section-subtitle">
              Structured DFIR approach aligned with NIST & industry best practices
            </p>
          </div>

          <div className="process-dfir-steps">
            <div className="process-dfir-cards-row">
              <div className="process-dfir-card animate-card">
                <span className="process-dfir-card-number">01</span>
                <h3 className="process-dfir-card-title">Identification</h3>
                <p className="section-content">
                  Confirm incident, identify attack vectors, impacted assets, and initial compromise indicators.
                </p>
              </div>
              <div className="process-dfir-card animate-card">
                <span className="process-dfir-card-number">02</span>
                <h3 className="process-dfir-card-title">Containment</h3>
                <p className="section-content">
                  Isolate compromised systems, block attacker access, and prevent lateral movement.
                </p>
              </div>
              <div className="process-dfir-card animate-card">
                <span className="process-dfir-card-number">03</span>
                <h3 className="process-dfir-card-title">Forensic Analysis</h3>
                <p className="section-content">
                  Timeline reconstruction, attacker activity mapping, and root cause analysis.
                </p>
              </div>
              <div className="process-dfir-card animate-card">
                <span className="process-dfir-card-number">04</span>
                <h3 className="process-dfir-card-title">Eradication & Recovery</h3>
                <p className="section-content">
                  Remove persistence mechanisms, restore systems, and validate security posture.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="compliance-dfir">
        <div className="compliance-dfir-container">
          <h2 className="section-title">Compliance & Legal Alignment</h2>

          <table className="compliance-dfir-table">
            <thead>
              <tr>
                <th>Framework / Regulation</th>
                <th>DFIR Coverage</th>
                <th>Support</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>ISO 27001</td>
                <td>Incident response, logging, evidence handling</td>
                <td>Audit Ready</td>
              </tr>
              <tr>
                <td>SOC 2 Type II</td>
                <td>Security incidents & response effectiveness</td>
                <td>Evidence Support</td>
              </tr>
              <tr>
                <td>DPDP / GDPR</td>
                <td>Breach investigation & notification</td>
                <td>Regulatory Defense</td>
              </tr>
              <tr>
                <td>CERT-In</td>
                <td>Mandatory incident reporting</td>
                <td>India Compliance</td>
              </tr>
              <tr>
                <td>NIST IR</td>
                <td>Incident response lifecycle</td>
                <td>Best Practice</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-dfir">
        <h2 className="section-title">
          Respond Fast. Investigate Deep. Recover Securely.
        </h2>
        <p className="section-subtitle">
          Engage Cysigil’s DFIR experts to manage cyber incidents with speed,
          precision, and legal defensibility.
        </p>
        <a href="#contact">
          <div className="btn-primary btn btn-lg">
            <span>Activate DFIR Support</span>
          </div>
        </a>
      </section>

      {/* ANIMATIONS */}
      <Script
        html={`<script defer>
(function(){
  const items = document.querySelectorAll(
    ".features-dfir-item, .process-dfir-step"
  )
  const observer = new IntersectionObserver((entries)=>{
    entries.forEach(e=>{
      if(e.isIntersecting){
        e.target.style.opacity="1"
        e.target.style.transform="translateY(0)"
      }
    })
  },{threshold:0.15})

  items.forEach((el,i)=>{
    el.style.opacity="0"
    el.style.transform="translateY(20px)"
    el.style.transition=\0.5s ease \${i*0.1}s\
    observer.observe(el)
  })
})()
</script>`}
      />

      <Footer />
    </div>
  )
}

export default DigitalForensics